import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { useAppSelector } from 'common/hooks/redux'
import { ContentLibrarySortBy } from 'features/unifiedFlow/contentsPage/interfaces'

import {
  ContentLibraryEntryResponse,
  ContentLibraryResponse,
  getContentLibrary,
} from '../api'
import { contentKeys } from '../queryKeys'

export type ContentLibraryElement = {
  id: number
  name: string
  project?: { id: string; name: string }
  template: { id: string; name: string }
  campaignId: string
  experimentType: string
  owner: { id: string; name: string }
  createdDate: string
  sendDate: string
  status: string
  endDate?: string
}

export type ContentLibraryEntry = {
  id: string
  name: string
  status: string
  createdDate: string
  isLegacy: boolean
  isPinned: boolean
  createdByApi: boolean
  subRows: ContentLibraryElement[]
}

const useGetContentLibraryQuery = ({
  offset,
  limit,
  sortBy,
  searchValue,
}: {
  offset?: number
  limit?: number
  sortBy?: ContentLibrarySortBy
  searchValue: string
}) => {
  const queryClient = useQueryClient()
  const { pinContent: isPinContentEnabled } = useFlags()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const {
    selectedProjects,
    selectedOwners,
    selectedTypes,
    selectedTemplates,
    selectedStatuses,
  } = useAppSelector((state) => state.unifiedFlow.contentLibraryFilters)

  const mapContentLibraryEntry = (
    item: ContentLibraryEntryResponse,
    isPinned: boolean
  ): ContentLibraryEntry => ({
    id: item.id,
    name: item.name,
    status: item.status,
    createdDate: item.createdDate,
    isLegacy: item.is_legacy,
    isPinned,
    createdByApi: item.created_by_api,
    subRows: item.elements.map((element) => ({
      id: element.id,
      name: element.name,
      project: element.project ?? undefined,
      template: element.template,
      campaignId: element.campaign_id,
      experimentType: element.experimentType,
      createdByApi: item.created_by_api,
      owner: element.owner,
      createdDate: element.createdDate,
      sendDate: element.sendDate ?? '',
      endDate: element.endDate ?? '',
      status: element.status,
    })),
  })

  const getContentLibraryQuery = useQuery<
    ContentLibraryResponse,
    unknown,
    {
      data: ContentLibraryEntry[]
      total: number
      pinnedContents: ContentLibraryEntry[]
    }
  >(
    contentKeys.contentLibrary({
      accountId,
      offset,
      limit,
      sortBy,
      searchValue,
      selectedProjects,
      selectedOwners,
      selectedTypes,
      selectedTemplates,
      selectedStatuses,
    }),
    () =>
      getContentLibrary({
        accountId,
        offset,
        limit,
        sortBy,
        searchValue,
        selectedProjects,
        selectedOwners,
        selectedTypes,
        selectedTemplates,
        selectedStatuses,
      }),
    {
      select: (data) => {
        const pinnedContents = isPinContentEnabled
          ? data.pinnedContents ?? []
          : []

        return {
          data: data.data.map((item) =>
            mapContentLibraryEntry(
              item,
              pinnedContents.some(
                (pinnedContent) => pinnedContent.id === item.id
              )
            )
          ),
          pinnedContents: pinnedContents.map((item) =>
            mapContentLibraryEntry(item, true)
          ),
          total: data.total,
        }
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )

  // Prefetch next page
  useEffect(() => {
    if (getContentLibraryQuery.data) {
      const nextOffset = (offset || 0) + (limit || 10)
      const total = getContentLibraryQuery.data.total

      if (nextOffset < total) {
        queryClient.prefetchQuery(
          contentKeys.contentLibrary({
            accountId,
            offset: nextOffset,
            limit,
            sortBy,
            searchValue,
            selectedProjects,
            selectedOwners,
            selectedTypes,
            selectedTemplates,
            selectedStatuses,
          }),
          () =>
            getContentLibrary({
              accountId,
              offset: nextOffset,
              limit,
              sortBy,
              searchValue,
              selectedOwners,
              selectedProjects,
              selectedTypes,
              selectedTemplates,
              selectedStatuses,
            })
        )
      }
    }
  }, [
    getContentLibraryQuery.data,
    accountId,
    offset,
    limit,
    queryClient,
    selectedProjects,
    selectedOwners,
    selectedTypes,
    selectedTemplates,
    selectedStatuses,
    sortBy,
    searchValue,
    isPinContentEnabled,
  ])

  return getContentLibraryQuery
}

export default useGetContentLibraryQuery
